import { useState, useEffect } from 'react';
import { FaCalculator, FaDollarSign, FaPercent, FaCalendarAlt, FaCalendarWeek, FaCalendarDay, FaPlusCircle, FaMoon, FaSun, FaSpinner } from 'react-icons/fa';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { useTheme } from '../../context/ThemeContext';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalculator } from "@fortawesome/free-solid-svg-icons";




export default function LoanCalculator() {
  
  const { darkMode, toggleDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    rate: '',
    term: '',
    frequency: '12',
    startDate: new Date().toISOString().split('T')[0],
    extra: ''
  });
  const [results, setResults] = useState({
    payment: '$0.00',
    interest: '$0.00',
    cost: '$0.00',
    time: '0 years',
    amortization: []
  });

  // Load saved data from localStorage
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('loanData'));
    if (saved) {
      setFormData(prev => ({
        ...prev,
        ...saved,
        startDate: saved.startDate || new Date().toISOString().split('T')[0]
      }));
      
      if (saved.amount && saved.rate && saved.term) {
        calculateLoan(saved);
      }
    }
  }, []);

  // Toggle dark mode
  

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  // Handle input changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  // Calculate loan details
  const calculateLoan = (data = formData) => {
    setLoading(true);
    
    setTimeout(() => {
      try {
        const amount = parseFloat(data.amount);
        const annualRate = parseFloat(data.rate);
        const term = parseInt(data.term);
        const frequency = parseInt(data.frequency);
        const extra = parseFloat(data.extra) || 0;
        
        if (amount <= 0 || annualRate <= 0 || term <= 0) {
          alert('Please enter valid loan amount, interest rate, and term');
          return;
        }
        
        const periods = term * frequency;
        const rate = annualRate / 100 / frequency;
        const payment = (amount * rate) / (1 - Math.pow(1 + rate, -periods));
        
        let balance = amount;
        let totalInterest = 0;
        const amortization = [];
        
        for (let i = 1; i <= periods; i++) {
          const interest = balance * rate;
          const principal = (payment + extra) - interest;
          balance -= principal;
          if (balance < 0) balance = 0;
          totalInterest += interest;
          
          amortization.push({
            period: i,
            payment: payment + extra,
            principal,
            interest,
            balance
          });
          
          if (balance === 0) break;
        }
        
        const totalPayment = payment * amortization.length + extra * amortization.length;
        
        // Update results
        setResults({
          payment: formatCurrency(payment),
          interest: formatCurrency(totalInterest),
          cost: formatCurrency(totalPayment),
          time: `${(amortization.length / frequency).toFixed(1)} years`,
          amortization
        });
        
        // Save to localStorage
        localStorage.setItem('loanData', JSON.stringify({
          ...data,
          startDate: data.startDate
        }));
        
      } catch (error) {
        console.error('Calculation error:', error);
        alert('An error occurred during calculation. Please check your inputs.');
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  return (
    <>
      <HeaderSection toolId="loanAmortizationCalculator" />
      <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4">
    <main className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
       <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                                <h1 className="text-2xl font-bold mb-2 flex items-center">
                                    <FontAwesomeIcon
                                        icon={faCalculator}
                                        className="mr-3"
                                    />
                                    Loan Amortization Calculator
                                </h1>
                                <p className="opacity-90">
                                 Calculate your loan payments with comprehensive
                                 validation checks
                                </p>
                            </div>
      <article className="container mx-auto px-4 py-8">
        <section className={`max-w-4xl mx-auto rounded-2xl  ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="relative p-8">
            {/* Header */}
            <header className="text-center mb-8">
              <h1 className={`text-3xl font-bold mb-2 inline-flex items-center ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                <FaCalculator className="mr-3" aria-hidden="true" />
                Loan Amortization Calculator
              </h1>
              <div className="h-1 w-20 mx-auto bg-green-400 rounded-full" aria-hidden="true"></div>
            </header>

            {/* Calculator Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Input Section */}
              <section className="space-y-6">
                <form onSubmit={(e) => {
                  e.preventDefault();
                  calculateLoan();
                }}>
                  <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Loan Amount */}
                    <div className="space-y-2">
                      <label htmlFor="amount" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Loan Amount
                      </label>
                      <div className="relative">
                        <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                          <FaDollarSign aria-hidden="true" />
                        </div>
                        <input
                          type="number"
                          id="amount"
                          value={formData.amount}
                          onChange={handleChange}
                          placeholder="e.g. 250000"
                          min="0"
                          step="1000"
                          className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border-gray-300 text-gray-800'}`}
                          aria-describedby="amount-description"
                        />
                      </div>
                      <p id="amount-description" className="sr-only">Enter the total loan amount in dollars</p>
                    </div>

                    {/* Interest Rate */}
                    <div className="space-y-2">
                      <label htmlFor="rate" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Interest Rate (%)
                      </label>
                      <div className="relative">
                        <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                          <FaPercent aria-hidden="true" />
                        </div>
                        <input
                          type="number"
                          id="rate"
                          value={formData.rate}
                          onChange={handleChange}
                          placeholder="e.g. 3.5"
                          min="0"
                          max="100"
                          step="0.01"
                          className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border-gray-300 text-gray-800'}`}
                          aria-describedby="rate-description"
                        />
                      </div>
                      <p id="rate-description" className="sr-only">Enter the annual interest rate percentage</p>
                    </div>

                    {/* Loan Term */}
                    <div className="space-y-2">
                      <label htmlFor="term" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Loan Term (Years)
                      </label>
                      <div className="relative">
                        <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                          <FaCalendarAlt aria-hidden="true" />
                        </div>
                        <input
                          type="number"
                          id="term"
                          value={formData.term}
                          onChange={handleChange}
                          placeholder="e.g. 30"
                          min="1"
                          max="50"
                          step="1"
                          className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border-gray-300 text-gray-800'}`}
                          aria-describedby="term-description"
                        />
                      </div>
                      <p id="term-description" className="sr-only">Enter the loan term in years</p>
                    </div>

                    {/* Payment Frequency */}
                    <div className="space-y-2">
                      <label htmlFor="frequency" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Payment Frequency
                      </label>
                      <div className="relative">
                        <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                          <FaCalendarWeek aria-hidden="true" />
                        </div>
                        <select
                          id="frequency"
                          value={formData.frequency}
                          onChange={handleChange}
                          className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border-gray-300 text-gray-800'}`}
                          aria-describedby="frequency-description"
                        >
                          <option value="12">Monthly</option>
                          <option value="24">Semi-Monthly</option>
                          <option value="26">Bi-Weekly</option>
                          <option value="52">Weekly</option>
                        </select>
                      </div>
                      <p id="frequency-description" className="sr-only">Select how often payments will be made</p>
                    </div>
                  </fieldset>

                  {/* Start Date */}
                  <div className="space-y-2">
                    <label htmlFor="startDate" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Start Date
                    </label>
                    <div className="relative">
                      <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                        <FaCalendarDay aria-hidden="true" />
                      </div>
                      <input
                        type="date"
                        id="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border-gray-300 text-gray-800'}`}
                        aria-describedby="date-description"
                      />
                    </div>
                    <p id="date-description" className="sr-only">Select the loan start date</p>
                  </div>

                  {/* Extra Payment */}
                  <div className="space-y-2">
                    <label htmlFor="extra" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Extra Payment
                    </label>
                    <div className="relative">
                      <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                        <FaPlusCircle aria-hidden="true" />
                      </div>
                      <input
                        type="number"
                        id="extra"
                        value={formData.extra}
                        onChange={handleChange}
                        placeholder="e.g. 100"
                        min="0"
                        step="10"
                        className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border-gray-300 text-gray-800'}`}
                        aria-describedby="extra-description"
                      />
                    </div>
                    <p id="extra-description" className="sr-only">Enter any additional payment amount</p>
                  </div>

                  {/* Calculate Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full mt-6 py-3 px-6 rounded-lg font-semibold text-white bg-green-500 hover:bg-green-600 shadow-md dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 transition-all duration-300 flex items-center justify-center ${loading ? 'animate-pulse' : ''}`}
                    aria-live="polite"
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="animate-spin mr-3" aria-hidden="true" />
                        <span>Calculating...</span>
                      </>
                    ) : (
                      <>
                        <FaCalculator className="mr-3" aria-hidden="true" />
                        <span>Calculate</span>
                      </>
                    )}
                  </button>
                </form>
              </section>

              {/* Results Preview */}
              <aside className={`p-6 rounded-lg ${darkMode ? 'bg-gray-700/30 border border-gray-600' : 'bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 border border-gray-200'}`}>
                <h2 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  Loan Summary
                </h2>
                
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {/* Monthly Payment */}
                  <article className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 hover:border-green-400' : 'bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 hover:border-green-300'} transition-colors`}>
                    <h3 className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Monthly Payment</h3>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{results.payment}</p>
                  </article>
                  
                  {/* Total Interest */}
                  <article className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 hover:border-green-400' : 'bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 hover:border-green-300'} transition-colors`}>
                    <h3 className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Interest</h3>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{results.interest}</p>
                  </article>
                  
                  {/* Total Cost */}
                  <article className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 hover:border-green-400' : 'bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 hover:border-green-300'} transition-colors`}>
                    <h3 className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Cost</h3>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{results.cost}</p>
                  </article>
                  
                  {/* Payoff Time */}
                  <article className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 hover:border-green-400' : 'bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 hover:border-green-300'} transition-colors`}>
                    <h3 className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Payoff Time</h3>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{results.time}</p>
                  </article>
                </div>
                
                <p className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>
                  Enter your loan details and click Calculate to see the full amortization schedule
                </p>
              </aside>
            </div>

            {/* Amortization Table */}
            {results.amortization.length > 0 && (
              <section className="mt-12" aria-labelledby="amortization-heading">
                <h2 id="amortization-heading" className={`text-xl font-semibold mb-4 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  Amortization Schedule
                </h2>
                
                <div className={`rounded-lg overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <caption className="sr-only">Loan payment breakdown by period</caption>
                      <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <tr>
                          <th scope="col" className={`py-3 px-4 text-left ${darkMode ? 'text-green-400' : 'text-green-600'}`}>#</th>
                          <th scope="col" className={`py-3 px-4 text-left ${darkMode ? 'text-green-400' : 'text-green-600'}`}>Payment</th>
                          <th scope="col" className={`py-3 px-4 text-left ${darkMode ? 'text-green-400' : 'text-green-600'}`}>Principal</th>
                          <th scope="col" className={`py-3 px-4 text-left ${darkMode ? 'text-green-400' : 'text-green-600'}`}>Interest</th>
                          <th scope="col" className={`py-3 px-4 text-left ${darkMode ? 'text-green-400' : 'text-green-600'}`}>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.amortization.map((row, index) => (
                          <tr 
                            key={index} 
                            className={`border-t ${darkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 hover:bg-gray-50'}`}
                          >
                            <th scope="row" className="py-3 px-4">{row.period}</th>
                            <td className="py-3 px-4">{formatCurrency(row.payment)}</td>
                            <td className="py-3 px-4">{formatCurrency(row.principal)}</td>
                            <td className="py-3 px-4">{formatCurrency(row.interest)}</td>
                            <td className="py-3 px-4">{formatCurrency(row.balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}
          </div>
        </section>
      </article>

      {/* Dark Mode Toggle */}
      <button
        onClick={toggleDarkMode}
        className={`fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 transition-all ${darkMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-800 hover:bg-gray-900 text-white'}`}
        aria-label={`Toggle ${darkMode ? 'light' : 'dark'} mode`}
      >
        {darkMode ? <FaSun size={20} aria-hidden="true" /> : <FaMoon size={20} aria-hidden="true" />}
      </button>
    </main>
    </div>
          <BottomSection toolId="loanAmortizationCalculator" />
    </>
  );
}